import { useEffect, useMemo, useState } from 'react';
import { CheckSquare, Loader2, MessageCircle, Phone, RefreshCw, Search } from 'lucide-react';
import { offerService } from '../services/offerService';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { toast } from 'sonner';

type OfferStatus = 'new' | 'approved' | 'rejected';

interface WorkiomOfferRow {
  workiomRecordId: string | null;
  offerNo: string | null;
  shipmentCode: string | null;
  driverName: string | null;
  tractorPlate: string | null;
  trailerPlate: string | null;
  driverPhone: string | null;
  offerPrice: number;
  vehicleArrivalTime: string | null;
  offerStatusLabel: string | null;
  status: OfferStatus;
  createdAt: string | null;
  updatedAt: string | null;
  syncedAt: string | null;
  pickupDate?: string | null;
  pickupCity?: string | null;
  pickupDistrict?: string | null;
  pickupName?: string | null;
  deliveryCity?: string | null;
  deliveryDistrict?: string | null;
  deliveryName?: string | null;
  vehicleSpecType?: string | null;
  loadType?: string | null;
  palletCount?: number;
  weightKg?: number;
  distanceKm?: number;
  stopCount?: number;
  regionPrice?: number;
  offerTlPerKm?: number;
  description?: string | null;
}

interface ColumnFilters {
  offerNo: string;
  shipmentCode: string;
  driverName: string;
  tractorPlate: string;
  trailerPlate: string;
  driverPhone: string;
  statusLabel: string;
  pickup: string;
  delivery: string;
  vehicleSpecType: string;
  loadType: string;
  tonaj: string;
}

interface ListMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  withShipmentLink: number;
  incomplete: number;
  includeIncomplete: boolean;
}

interface EditFormState {
  workiomRecordId: string;
  offerNo: string;
  offerPrice: string;
  vehicleArrivalTime: string;
  tractorPlate: string;
  trailerPlate: string;
  driverPhone: string;
  status: 'new' | 'approved' | 'rejected';
}

const statusFilterOptions: Array<{ value: 'all' | OfferStatus; label: string }> = [
  { value: 'all', label: 'Tum Durumlar' },
  { value: 'new', label: 'Yeni Teklif' },
  { value: 'approved', label: 'Onaylandi' },
  { value: 'rejected', label: 'Reddedildi' },
];

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('tr-TR');
};

const normalizePhoneTR = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return { display: '', tel: '', wa: '' };
  }

  let digits = raw.replace(/\D/g, '');

  if (digits.startsWith('0') && digits.length === 11) {
    digits = digits.slice(1);
  }

  if (!digits.startsWith('90') && digits.length === 10) {
    digits = `90${digits}`;
  }

  const display = digits ? `+${digits}` : raw;
  const tel = digits ? `+${digits}` : '';
  const wa = digits || '';

  return { display, tel, wa };
};

export function OfferPortal() {
  const [rows, setRows] = useState<WorkiomOfferRow[]>([]);
  const [meta, setMeta] = useState<ListMeta>({
    total: 0,
    page: 1,
    pageSize: 100,
    totalPages: 1,
    withShipmentLink: 0,
    incomplete: 0,
    includeIncomplete: true,
  });
  const [loading, setLoading] = useState(true);
  const [syncingCache, setSyncingCache] = useState(false);
  const [syncingBids, setSyncingBids] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | OfferStatus>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [includeIncomplete, setIncludeIncomplete] = useState(true);
  const [offeredFrom, setOfferedFrom] = useState('');
  const [offeredTo, setOfferedTo] = useState('');
  const [pickupCity, setPickupCity] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
    offerNo: '',
    shipmentCode: '',
    driverName: '',
    tractorPlate: '',
    trailerPlate: '',
    driverPhone: '',
    statusLabel: '',
    pickup: '',
    delivery: '',
    vehicleSpecType: '',
    loadType: '',
    tonaj: '',
  });
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchRows = async () => {
    const response = await offerService.getWorkiomOffers({
      search: search.trim() || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      page,
      pageSize,
      includeIncomplete,
      offeredFrom: offeredFrom || undefined,
      offeredTo: offeredTo || undefined,
      pickupCity: pickupCity.trim() || undefined,
      deliveryCity: deliveryCity.trim() || undefined,
    });

    if (!response.success) {
      throw new Error(response.message || 'Workiom teklifleri alinamadi');
    }

    setRows((response.data || []) as WorkiomOfferRow[]);
    setMeta((((response as any).meta) || meta) as ListMeta);
  };

  const syncCacheFromWorkiom = async () => {
    setSyncingCache(true);
    setError(null);
    try {
      const cacheResponse = await offerService.syncWorkiomOffers();
      if (!cacheResponse.success) {
        throw new Error(cacheResponse.message || 'Workiom cache guncellenemedi');
      }

      const bidsResponse = await offerService.syncWorkiomOffersToBids();
      if (!bidsResponse.success) {
        throw new Error(bidsResponse.message || 'Workiom teklifleri bizim tekliflere yazilamadi');
      }

      toast.success(bidsResponse.message || cacheResponse.message || 'Workiom teklifleri alindi ve bizim tekliflere yazildi');
      await fetchRows();
    } catch (err: any) {
      setError(err?.message || 'Workiom cache guncelleme hatasi');
      toast.error(err?.message || 'Workiom cache guncelleme hatasi');
    } finally {
      setSyncingCache(false);
      setLoading(false);
    }
  };

  const syncCachedOffersToBids = async () => {
    setSyncingBids(true);
    setError(null);
    try {
      const response = await offerService.syncWorkiomOffersToBids();
      if (!response.success) {
        throw new Error(response.message || 'Bids senkronu basarisiz');
      }
      toast.success(response.message || 'Bids senkronu tamamlandi');
    } catch (err: any) {
      setError(err?.message || 'Bids senkron hatasi');
      toast.error(err?.message || 'Bids senkron hatasi');
    } finally {
      setSyncingBids(false);
    }
  };

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        await fetchRows();
      } catch (err: any) {
        if (active) setError(err?.message || 'Veriler alinamadi');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [page, pageSize, includeIncomplete, statusFilter, offeredFrom, offeredTo, pickupCity, deliveryCity]);

  useAutoRefresh(() => fetchRows().catch((err) => setError(err?.message || 'Veriler alinamadi')), {
    intervalMs: 30000,
  });

  const stats = useMemo(() => ({
    total: meta.total,
    newCount: rows.filter((row) => row.status === 'new').length,
    approved: rows.filter((row) => row.status === 'approved').length,
    rejected: rows.filter((row) => row.status === 'rejected').length,
    totalPrice: rows.reduce((sum, row) => sum + Number(row.offerPrice || 0), 0),
  }), [rows, meta.total]);

  const getStatusBadge = (row: WorkiomOfferRow) => {
    if (row.status === 'approved') return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Onaylandi</span>;
    if (row.status === 'rejected') return <span className="px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">Reddedildi</span>;
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Yeni Teklif</span>;
  };

  const filteredRows = useMemo(() => {
    const norm = (v: string) => v.toLocaleLowerCase('tr-TR').trim();
    const contains = (value: unknown, q: string) => {
      if (!q) return true;
      return String(value || '').toLocaleLowerCase('tr-TR').includes(norm(q));
    };

    return rows.filter((row) => {
      const pickupText = [row.pickupName, row.pickupCity, row.pickupDistrict].filter(Boolean).join(' / ');
      const deliveryText = [row.deliveryName, row.deliveryCity, row.deliveryDistrict].filter(Boolean).join(' / ');
      return (
        contains(row.offerNo, columnFilters.offerNo)
        && contains(row.shipmentCode, columnFilters.shipmentCode)
        && contains(row.driverName, columnFilters.driverName)
        && contains(row.tractorPlate, columnFilters.tractorPlate)
        && contains(row.trailerPlate, columnFilters.trailerPlate)
        && contains(row.driverPhone, columnFilters.driverPhone)
        && contains(row.offerStatusLabel, columnFilters.statusLabel)
        && contains(pickupText, columnFilters.pickup)
        && contains(deliveryText, columnFilters.delivery)
        && contains(row.vehicleSpecType, columnFilters.vehicleSpecType)
        && contains(row.loadType, columnFilters.loadType)
        && contains(row.weightKg, columnFilters.tonaj)
      );
    });
  }, [rows, columnFilters]);

  const handleColumnFilterChange = (key: keyof ColumnFilters, value: string) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
  };

  const openEditForm = (row: WorkiomOfferRow) => {
    if (!row.workiomRecordId) return;
    setEditForm({
      workiomRecordId: row.workiomRecordId,
      offerNo: row.offerNo || '',
      offerPrice: String(row.offerPrice || 0),
      vehicleArrivalTime: row.vehicleArrivalTime || '',
      tractorPlate: row.tractorPlate || '',
      trailerPlate: row.trailerPlate || '',
      driverPhone: row.driverPhone || '',
      status: row.status || 'new',
    });
  };

  const submitEditForm = async () => {
    if (!editForm) return;
    try {
      setSavingEdit(true);
      const response = await offerService.updateWorkiomOffer(editForm.workiomRecordId, {
        offerNo: editForm.offerNo,
        offerPrice: Number(editForm.offerPrice || 0),
        vehicleArrivalTime: editForm.vehicleArrivalTime,
        tractorPlate: editForm.tractorPlate,
        trailerPlate: editForm.trailerPlate,
        driverPhone: editForm.driverPhone,
        status: editForm.status,
      });

      if (!response.success) {
        throw new Error(response.message || 'Teklif guncellenemedi');
      }

      toast.success('Teklif kaydi guncellendi');
      setEditForm(null);
      await fetchRows();
    } catch (err: any) {
      toast.error(err?.message || 'Teklif guncellenemedi');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Teklif Portali</h1>
          <p className="text-gray-500 mt-1">Workiom verisini cacheleyin, sonra isterseniz bids tablosuna senkronlayin.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={syncCacheFromWorkiom}
            disabled={syncingCache}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {syncingCache ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Workiom'dan Cek
          </button>
          <button
            onClick={syncCachedOffersToBids}
            disabled={syncingBids}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {syncingBids ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
            Bids'e Senkronla
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4"><div className="text-sm text-gray-500">Toplam (Filtreli)</div><div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div></div>
        <div className="bg-white rounded-lg border border-gray-200 p-4"><div className="text-sm text-gray-500">Bu Sayfa Yeni</div><div className="text-2xl font-bold text-blue-600 mt-1">{stats.newCount}</div></div>
        <div className="bg-white rounded-lg border border-gray-200 p-4"><div className="text-sm text-gray-500">Bu Sayfa Onayli</div><div className="text-2xl font-bold text-green-600 mt-1">{stats.approved}</div></div>
        <div className="bg-white rounded-lg border border-gray-200 p-4"><div className="text-sm text-gray-500">Bu Sayfa Red</div><div className="text-2xl font-bold text-rose-600 mt-1">{stats.rejected}</div></div>
        <div className="bg-white rounded-lg border border-gray-200 p-4"><div className="text-sm text-gray-500">Sayfa Toplam Tutar</div><div className="text-2xl font-bold text-purple-700 mt-1">{stats.totalPrice.toLocaleString('tr-TR')} TL</div></div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setPage(1);
                fetchRows().catch((err) => setError(err?.message || 'Arama hatasi'));
              }
            }}
            placeholder="Teklif no, sevk kodu, surucu, plaka..."
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => {
            setPage(1);
            fetchRows().catch((err) => setError(err?.message || 'Arama hatasi'));
          }}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Ara
        </button>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value as 'all' | OfferStatus);
            setPage(1);
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {statusFilterOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeIncomplete}
            onChange={(event) => {
              setIncludeIncomplete(event.target.checked);
              setPage(1);
            }}
          />
          Eslesmeyen kayitlari goster
        </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={pickupCity}
            onChange={(event) => {
              setPickupCity(event.target.value);
              setPage(1);
            }}
            placeholder="Yukleme ili"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <input
            value={deliveryCity}
            onChange={(event) => {
              setDeliveryCity(event.target.value);
              setPage(1);
            }}
            placeholder="Teslim ili"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={offeredFrom}
            onChange={(event) => {
              setOfferedFrom(event.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={offeredTo}
            onChange={(event) => {
              setOfferedTo(event.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="text-sm text-gray-600">
        Sevkiyat bagli: <strong>{meta.withShipmentLink}</strong> • Eslesmeyen: <strong>{meta.incomplete}</strong>
      </div>

      {loading && (
        <div className="bg-white rounded-lg border border-gray-200 py-16 flex justify-center">
          <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" />Yukleniyor...</div>
        </div>
      )}

      {!loading && error && (
        <div className="bg-white rounded-lg border border-rose-200 p-6 text-rose-700">
          <div className="font-medium mb-2">Veri alinamadi</div>
          <div className="text-sm">{error}</div>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="workiom-grid-wrap">
            <table className="workiom-grid-table text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Teklif No</th>
                  <th className="px-4 py-3 text-left">Sevk Kodu</th>
                  <th className="px-4 py-3 text-left">Teklif Tarihi</th>
                  <th className="px-4 py-3 text-left">Yukleme Ili</th>
                  <th className="px-4 py-3 text-left">Bosaltma Ili</th>
                  <th className="px-4 py-3 text-left">Yukleme Musterisi</th>
                  <th className="px-4 py-3 text-left">Bosaltma Musterisi</th>
                  <th className="px-4 py-3 text-left">Sefer Km</th>
                  <th className="px-4 py-3 text-left">Teklif TL/Km</th>
                  <th className="px-4 py-3 text-left">Ugrama Sayisi</th>
                  <th className="px-4 py-3 text-left">Bolge Fiyati</th>
                  <th className="px-4 py-3 text-left">Urun Turu</th>
                  <th className="px-4 py-3 text-left">Surucu</th>
                  <th className="px-4 py-3 text-left">Cekici</th>
                  <th className="px-4 py-3 text-left">Dorse</th>
                  <th className="px-4 py-3 text-left">Telefon</th>
                  <th className="px-4 py-3 text-left">Teklif Tutari</th>
                  <th className="px-4 py-3 text-left">Teklif Statusu</th>
                  <th className="px-4 py-3 text-left">Aciklama</th>
                  <th className="px-4 py-3 text-left">Aracin Gelecegi Zaman</th>
                  <th className="px-4 py-3 text-left">Yukleme Tarihi</th>
                  <th className="px-4 py-3 text-left">Arac Turu</th>
                  <th className="px-4 py-3 text-left">Tonaj</th>
                  <th className="px-4 py-3 text-left">Palet</th>
                  <th className="px-4 py-3 text-left">Son Guncelleme</th>
                  <th className="px-4 py-3 text-left">Islem</th>
                </tr>
                <tr>
                  <th className="px-3 py-2"></th>
                  <th className="px-4 py-2"><input value={columnFilters.offerNo} onChange={(e) => handleColumnFilterChange('offerNo', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"><input value={columnFilters.shipmentCode} onChange={(e) => handleColumnFilterChange('shipmentCode', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"><input value={columnFilters.driverName} onChange={(e) => handleColumnFilterChange('driverName', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"><input value={columnFilters.tractorPlate} onChange={(e) => handleColumnFilterChange('tractorPlate', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"><input value={columnFilters.trailerPlate} onChange={(e) => handleColumnFilterChange('trailerPlate', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"><input value={columnFilters.driverPhone} onChange={(e) => handleColumnFilterChange('driverPhone', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"><input value={columnFilters.statusLabel} onChange={(e) => handleColumnFilterChange('statusLabel', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"><input value={columnFilters.pickup} onChange={(e) => handleColumnFilterChange('pickup', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"><input value={columnFilters.delivery} onChange={(e) => handleColumnFilterChange('delivery', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"><input value={columnFilters.vehicleSpecType} onChange={(e) => handleColumnFilterChange('vehicleSpecType', e.target.value)} placeholder="Filtre" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" /></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRows.map((row, index) => {
                  const phone = normalizePhoneTR(row.driverPhone);
                  const whatsappMessage = encodeURIComponent(`Merhaba, ${row.shipmentCode || row.offerNo || 'sevkiyat'} icin teklif detayini konusmak istiyorum.`);

                  return (
                    <tr key={row.workiomRecordId || `${row.offerNo}-${row.driverName}`} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-gray-500">{(meta.page - 1) * meta.pageSize + index + 1}</td>
                      <td className="px-4 py-3 font-medium">{row.offerNo || '-'}</td>
                      <td className="px-4 py-3">{row.shipmentCode || '-'}</td>
                      <td className="px-4 py-3">{formatDateTime(row.createdAt)}</td>
                      <td className="px-4 py-3">{row.pickupCity || '-'}</td>
                      <td className="px-4 py-3">{row.deliveryCity || '-'}</td>
                      <td className="px-4 py-3">{row.pickupName || '-'}</td>
                      <td className="px-4 py-3">{row.deliveryName || '-'}</td>
                      <td className="px-4 py-3">{Number(row.distanceKm || 0).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3">{Number(row.offerTlPerKm || 0).toLocaleString('tr-TR')} TL</td>
                      <td className="px-4 py-3">{Number(row.stopCount || 0).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3">{Number(row.regionPrice || 0).toLocaleString('tr-TR')} TL</td>
                      <td className="px-4 py-3">{row.loadType || '-'}</td>
                      <td className="px-4 py-3">{row.driverName || '-'}</td>
                      <td className="px-4 py-3">{row.tractorPlate || '-'}</td>
                      <td className="px-4 py-3">{row.trailerPlate || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="group inline-flex items-center gap-2">
                          <span>{phone.display || '-'}</span>
                          {phone.tel && (
                            <a
                              href={`tel:${phone.tel}`}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-sky-600 hover:text-sky-700"
                              title="Ara"
                            >
                              <Phone className="w-4 h-4" />
                            </a>
                          )}
                          {phone.wa && (
                            <a
                              href={`https://wa.me/${phone.wa}?text=${whatsappMessage}`}
                              target="_blank"
                              rel="noreferrer"
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-green-600 hover:text-green-700"
                              title="WhatsApp"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium">{Number(row.offerPrice || 0).toLocaleString('tr-TR')} TL</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(row)}
                          <span className="text-xs text-gray-500">{row.offerStatusLabel || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{row.description || '-'}</td>
                      <td className="px-4 py-3">{row.vehicleArrivalTime || '-'}</td>
                      <td className="px-4 py-3">{formatDateTime(row.pickupDate)}</td>
                      <td className="px-4 py-3">{row.vehicleSpecType || '-'}</td>
                      <td className="px-4 py-3">{Number(row.weightKg || 0).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3">{Number(row.palletCount || 0).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3">{formatDateTime(row.updatedAt || row.createdAt || row.syncedAt)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openEditForm(row)}
                          disabled={!row.workiomRecordId}
                          className="px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 disabled:opacity-50"
                        >
                          Duzenle
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredRows.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-500">Filtreye uygun teklif kaydi bulunamadi.</div>
          )}

          {rows.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {(meta.page - 1) * meta.pageSize + 1}-{Math.min(meta.page * meta.pageSize, meta.total)} / {meta.total}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={String(pageSize)}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                >
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                </select>
                <button
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={meta.page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                >
                  Geri
                </button>
                <span className="text-sm text-gray-700">{meta.page} / {meta.totalPages}</span>
                <button
                  onClick={() => setPage((prev) => Math.min(meta.totalPages, prev + 1))}
                  disabled={meta.page >= meta.totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50"
                >
                  Ileri
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {editForm && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Teklif Kaydi Duzenle</h3>
              <button onClick={() => setEditForm(null)} className="text-gray-500 hover:text-gray-700">Kapat</button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={editForm.offerNo} onChange={(e) => setEditForm((s) => s ? { ...s, offerNo: e.target.value } : s)} placeholder="Teklif No" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input value={editForm.offerPrice} onChange={(e) => setEditForm((s) => s ? { ...s, offerPrice: e.target.value } : s)} placeholder="Teklif Tutari" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input value={editForm.vehicleArrivalTime} onChange={(e) => setEditForm((s) => s ? { ...s, vehicleArrivalTime: e.target.value } : s)} placeholder="Aracin Gelecegi Zaman" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <select value={editForm.status} onChange={(e) => setEditForm((s) => s ? { ...s, status: e.target.value as 'new' | 'approved' | 'rejected' } : s)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="new">Yeni Teklif</option>
                <option value="approved">Teklif Onaylandi</option>
                <option value="rejected">Teklif Reddedildi</option>
              </select>
              <input value={editForm.tractorPlate} onChange={(e) => setEditForm((s) => s ? { ...s, tractorPlate: e.target.value } : s)} placeholder="Cekici" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input value={editForm.trailerPlate} onChange={(e) => setEditForm((s) => s ? { ...s, trailerPlate: e.target.value } : s)} placeholder="Dorse" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input value={editForm.driverPhone} onChange={(e) => setEditForm((s) => s ? { ...s, driverPhone: e.target.value } : s)} placeholder="Telefon" className="border border-gray-300 rounded-lg px-3 py-2 text-sm md:col-span-2" />
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button onClick={() => setEditForm(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Vazgec</button>
              <button onClick={submitEditForm} disabled={savingEdit} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                {savingEdit ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
